import React from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "../colors";
import { DISCORD_INVITE_URL } from "../../../public-site/siteData";
import { useAuthState } from "../../../app/AuthStateContext";
import { getSupportTicketInitialState } from "../../../support/leaverPenaltyAppeal";
import { mapApiCategoryToUi, submitSupportTicket } from "../../../support/submitSupportTicket";

const MotionBox = motion(Box);

const MAX_FILES = 5;
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const MIN_SUBJECT_LENGTH = 3;
const MIN_DESCRIPTION_LENGTH = 10;
const HERO_BG = "#080808";
const DANGER = "#dc2626";
const GOLD_GRADIENT = "linear-gradient(180deg, #f7cb79 0%, #f0b458 40%, #de8f2c 100%)";
const fieldGroupSx = { mb: 4 };
const COLUMN_DIVIDER_OFFSET_PX = 16;

const btnGhostSx = {
  fontFamily: "'Inter', sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.secondary,
  px: "28px",
  py: "13px",
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.08)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.01), transparent)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  cursor: "pointer",
  transition: "color .2s, border-color .2s, transform .25s ease",
  "&:hover": {
    color: colors.text.primary,
    borderColor: "rgba(232,163,61,0.35)",
    transform: "translateY(-2px)",
  },
};

const REVEAL = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.1 },
};

const REVEAL_TRANSITION = { duration: 0.8, ease: [0.22, 1, 0.36, 1] };

const HERO_ENTER = {
  initial: { y: 26, opacity: 0, filter: "blur(10px)" },
  animate: { y: 0, opacity: 1, filter: "blur(0px)" },
};

const CATEGORIES = [
  { key: "account", label: "Account" },
  { key: "billing", label: "Billing" },
  { key: "matchmaking", label: "Matchmaking" },
  { key: "report", label: "Player report" },
  { key: "technical", label: "Technical" },
  { key: "other", label: "Other" },
];

const TRUST_ITEMS = [
  {
    label: "Human reviewed",
    body: "Every ticket is read by a real team member - no automated rejections.",
    icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  },
  {
    label: "Response within 24h",
    body: "Business day turnaround. Complex issues escalated immediately.",
    icon: (
      <>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </>
    ),
  },
  {
    label: "Confidential",
    body: "Your report and personal data are handled under our GDPR policy.",
    icon: (
      <>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </>
    ),
  },
  {
    label: "Tied to your account",
    body: "Logged-in tickets are linked to your profile for faster resolution.",
    icon: (
      <>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
  },
];

/** Aurora + scan lines (full viewport width, no grid/watermark). */
function SupportHeroEffects() {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        top: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: "100vw",
        height: "100%",
        minHeight: "100svh",
        overflow: "hidden",
        zIndex: 0,
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          "@keyframes auroradrift1": {
            "0%": { transform: "translate(0%, 0%) scale(1)" },
            "33%": { transform: "translate(6%, 10%) scale(1.1)" },
            "66%": { transform: "translate(-5%, 6%) scale(0.93)" },
            "100%": { transform: "translate(0%, 0%) scale(1)" },
          },
          "@keyframes auroradrift2": {
            "0%": { transform: "translate(0%, 0%) scale(1)" },
            "40%": { transform: "translate(-8%, -6%) scale(1.08)" },
            "70%": { transform: "translate(5%, -10%) scale(0.96)" },
            "100%": { transform: "translate(0%, 0%) scale(1)" },
          },
          "@keyframes auroradrift3": {
            "0%": { transform: "translate(0%, 0%) scale(1)" },
            "50%": { transform: "translate(10%, 5%) scale(1.06)" },
            "100%": { transform: "translate(0%, 0%) scale(1)" },
          },
        }}
      >
        {[
          {
            animation: "auroradrift1 22s ease-in-out infinite",
            background:
              "radial-gradient(ellipse at 85% 45%, rgba(232,163,61,0.1) 0%, transparent 60%), " +
              "radial-gradient(ellipse at 5% 70%, rgba(232,163,61,0.05) 0%, transparent 55%)",
            width: "140%",
            height: "320px",
            top: "-120px",
            left: "-20%",
            filter: "blur(48px)",
          },
          {
            animation: "auroradrift2 28s ease-in-out infinite",
            background:
              "radial-gradient(ellipse at 50% 100%, rgba(220,38,38,0.06) 0%, transparent 50%), " +
              "radial-gradient(ellipse at 60% 40%, rgba(246,197,111,0.08) 0%, transparent 60%)",
            width: "120%",
            height: "260px",
            top: "-80px",
            left: "-10%",
            filter: "blur(56px)",
          },
          {
            animation: "auroradrift3 34s ease-in-out infinite",
            background: "radial-gradient(ellipse at 50% 30%, rgba(255,216,154,0.06) 0%, transparent 55%)",
            width: "100%",
            height: "200px",
            top: "-50px",
            left: "0%",
            filter: "blur(60px)",
          },
        ].map((style, i) => (
          <Box key={i} sx={{ position: "absolute", ...style }} />
        ))}
      </Box>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.5,
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(232,163,61,0.02) 3px, rgba(232,163,61,0.02) 4px)",
          "@keyframes scanMove": {
            from: { backgroundPosition: "0 0" },
            to: { backgroundPosition: "0 400px" },
          },
          animation: "scanMove 8s linear infinite",
        }}
      />
    </Box>
  );
}

function Icon({ children, size = 16 }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      sx={{ width: size, height: size }}
    >
      {children}
    </Box>
  );
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileTypeClass(type) {
  if (type.startsWith("image/")) return "img";
  if (type.startsWith("video/")) return "vid";
  return "doc";
}

function fileTypeIcon(type) {
  if (type.startsWith("image/")) {
    return (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </>
    );
  }
  if (type.startsWith("video/")) {
    return (
      <>
        <polygon points="23 7 16 12 23 17 23 7" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
      </>
    );
  }
  return (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </>
  );
}

export default function SupportPage() {
  const { user } = useAuthState();
  const location = useLocation();
  const history = useHistory();
  const supportTicketInitialState = React.useMemo(
    () =>
      getSupportTicketInitialState({
        userEmail: user?.email || "",
        search: location.search,
        state: location.state,
      }),
    [location.search, location.state, user?.email]
  );

  const [category, setCategory] = React.useState(() =>
    mapApiCategoryToUi(supportTicketInitialState.form.category)
  );
  const [email, setEmail] = React.useState(supportTicketInitialState.form.email);
  const [subject, setSubject] = React.useState(supportTicketInitialState.form.subject);
  const [description, setDescription] = React.useState(supportTicketInitialState.form.description);
  const [appeal, setAppeal] = React.useState(supportTicketInitialState.appeal);
  const [attachments, setAttachments] = React.useState([]);
  const [fileError, setFileError] = React.useState("");
  const [submitError, setSubmitError] = React.useState("");
  const [dragOver, setDragOver] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [ticketNumber, setTicketNumber] = React.useState("");
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [touched, setTouched] = React.useState({
    email: false,
    subject: false,
    description: false,
  });

  const fileInputRef = React.useRef(null);
  const rightColumnRef = React.useRef(null);

  const syncColumnDivider = React.useCallback(() => {
    const column = rightColumnRef.current;
    const anchor = column?.querySelector("[data-support-form-anchor]");
    if (!column || !anchor) return;
    const top = anchor.offsetTop - COLUMN_DIVIDER_OFFSET_PX;
    column.style.setProperty("--support-divider-top", `${Math.max(0, top)}px`);
  }, []);

  React.useLayoutEffect(() => {
    syncColumnDivider();
    const column = rightColumnRef.current;
    if (!column) return undefined;
    const observer = new ResizeObserver(syncColumnDivider);
    observer.observe(column);
    window.addEventListener("resize", syncColumnDivider);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncColumnDivider);
    };
  }, [syncColumnDivider, isSuccess]);

  React.useEffect(() => {
    const next = getSupportTicketInitialState({
      userEmail: user?.email || "",
      search: location.search,
      state: location.state,
    });
    setCategory(mapApiCategoryToUi(next.form.category));
    setEmail(next.form.email);
    setSubject(next.form.subject);
    setDescription(next.form.description);
    setAppeal(next.appeal);
    setSubmitError("");
    setIsSuccess(false);
    setTicketNumber("");
  }, [location.search, location.state, user?.email]);

  React.useEffect(() => {
    if (!user?.email) return;
    setEmail((prev) => (prev === user.email ? prev : user.email));
  }, [user?.email]);

  const trimmedEmail = email.trim();
  const trimmedSubject = subject.trim();
  const trimmedDescription = description.trim();
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);

  const errors = {
    email:
      !trimmedEmail
        ? "Email is required."
        : !emailOk
          ? "Enter a valid email address."
          : "",
    subject:
      !trimmedSubject
        ? "Subject is required."
        : trimmedSubject.length < MIN_SUBJECT_LENGTH
          ? `Subject must be at least ${MIN_SUBJECT_LENGTH} characters.`
          : "",
    description:
      !trimmedDescription
        ? "Description is required."
        : trimmedDescription.length < MIN_DESCRIPTION_LENGTH
          ? `Description must be at least ${MIN_DESCRIPTION_LENGTH} characters.`
          : "",
  };

  const formValid =
    emailOk &&
    trimmedSubject.length >= MIN_SUBJECT_LENGTH &&
    trimmedDescription.length >= MIN_DESCRIPTION_LENGTH;

  const showValidationAlert = submitAttempted && !formValid;

  const addFiles = React.useCallback((filesToAdd) => {
    if (!filesToAdd?.length) return;
    setFileError("");
    setAttachments((prev) => {
      const next = [...prev];
      for (const file of filesToAdd) {
        if (next.length >= MAX_FILES) {
          setFileError("Maximum 5 files allowed.");
          break;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
          setFileError(`"${file.name}" exceeds 20 MB limit.`);
          continue;
        }
        const duplicate = next.some((f) => f.name === file.name && f.size === file.size);
        if (!duplicate) next.push(file);
      }
      return next;
    });
  }, []);

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const startNewTicket = React.useCallback(() => {
    history.replace("/support");
    setCategory("account");
    setEmail(user?.email || "");
    setSubject("");
    setDescription("");
    setAppeal(null);
    setAttachments([]);
    setFileError("");
    setSubmitError("");
    setTicketNumber("");
    setIsSuccess(false);
    setCopied(false);
    setSubmitAttempted(false);
    setTouched({ email: false, subject: false, description: false });
  }, [history, user?.email]);

  const copyTicketNumber = async () => {
    if (!ticketNumber) return;
    try {
      await navigator.clipboard.writeText(ticketNumber);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const onSubmit = async () => {
    if (isSubmitting) return;
    if (!formValid) {
      setSubmitAttempted(true);
      setSubmitError("");
      return;
    }

    setIsSubmitting(true);
    setSubmitAttempted(false);
    setSubmitError("");

    try {
      const data = await submitSupportTicket({
        category,
        email: trimmedEmail,
        subject: trimmedSubject,
        description: trimmedDescription,
        appeal,
        attachmentFiles: attachments,
      });

      if (data.ok) {
        setTicketNumber(data.ticket.ticketNumber);
        setIsSuccess(true);
      } else {
        setSubmitError(data.message);
      }
    } catch {
      setSubmitError("Could not reach the server. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: HERO_BG, color: colors.text.primary, overflowX: "hidden" }}>
      <Box sx={{ position: "relative", minHeight: "100svh", width: "100%" }}>
        <SupportHeroEffects />
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            minHeight: "100svh",
            maxWidth: 1400,
            mx: "auto",
            px: { xs: 2.5, md: 6 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(520px, 1.15fr)" },
          }}
        >
        <Box
          sx={{
            minWidth: 0,
            py: { xs: "80px", md: "120px" },
            pr: { xs: 0, md: 6 },
            borderBottom: { xs: `1px solid ${colors.border.subtle}`, md: "none" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <MotionBox {...HERO_ENTER} transition={REVEAL_TRANSITION}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 2.5 }}>
              <Box sx={{ width: 20, height: "1px", background: colors.primary.main }} />
              <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: colors.primary.main }}>
                Support
              </Typography>
            </Box>
          </MotionBox>
          <MotionBox
            component="h1"
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.1 }}
            sx={{
              fontSize: { xs: "2.2rem", md: "3.75rem" },
              fontWeight: 700,
              letterSpacing: "-0.04em",
              lineHeight: 0.95,
              textTransform: "uppercase",
              mb: 3,
              m: 0,
            }}
          >
            We&apos;ve
            <br />
            got you
            <br />
            <Box component="span" sx={{ background: GOLD_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              covered.
            </Box>
          </MotionBox>
          <MotionBox {...HERO_ENTER} transition={{ ...REVEAL_TRANSITION, delay: 0.2 }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 300, color: "rgba(240,236,228,0.52)", maxWidth: "30rem", lineHeight: 1.85, mb: 6 }}>
              Every report lands directly with our team - not a bot, not a queue that disappears.
              We read every ticket and reply within one business day.
            </Typography>
          </MotionBox>

          <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.08 }} sx={{ display: "flex", flexDirection: "column", gap: "2px", mb: 5 }}>
            {TRUST_ITEMS.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: "16px 20px",
                  background: "rgba(255,255,255,0.025)",
                  border: `1px solid ${colors.border.subtle}`,
                  transition: "border-color .25s",
                  "&:hover": { borderColor: "rgba(232,163,61,0.3)" },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `1px solid ${colors.border.subtle}`,
                    background: "rgba(232,163,61,0.06)",
                    color: colors.primary.main,
                  }}
                >
                  <Icon>{item.icon}</Icon>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(240,236,228,0.28)", mb: 0.3 }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "rgba(240,236,228,0.52)", lineHeight: 1.5 }}>
                    {item.body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </MotionBox>

          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.14 }}
            sx={{
              display: "flex",
              gap: 3.5,
              pt: 3.5,
              borderTop: `1px solid ${colors.border.subtle}`,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(240,236,228,0.28)", mb: 0.5 }}>
                Avg. response
              </Typography>
              <Typography sx={{ fontSize: "20px", fontWeight: 700, letterSpacing: "-0.02em" }}>&lt; 24h</Typography>
              <Typography sx={{ fontSize: "11px", color: "rgba(240,236,228,0.28)" }}>business days</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(240,236,228,0.28)", mb: 0.5 }}>
                Resolution rate
              </Typography>
              <Typography sx={{ fontSize: "20px", fontWeight: 700, letterSpacing: "-0.02em" }}>97%</Typography>
              <Typography sx={{ fontSize: "11px", color: "rgba(240,236,228,0.28)" }}>within 3 days</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(240,236,228,0.28)", mb: 0.5 }}>
                Also on
              </Typography>
              <Box component="a" href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer" sx={{ fontSize: "13px", color: colors.primary.main, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                Discord →
              </Box>
              <Typography sx={{ fontSize: "11px", color: "rgba(240,236,228,0.28)" }}>community support</Typography>
            </Box>
          </MotionBox>
        </Box>

        <Box
          ref={rightColumnRef}
          sx={{
            position: "relative",
            minWidth: 0,
            py: { xs: "48px", md: "100px" },
            pl: { xs: 0, md: 6 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            "&::before": {
              content: '""',
              display: { xs: "none", md: "block" },
              position: "absolute",
              left: 0,
              top: "var(--support-divider-top, 0px)",
              bottom: 0,
              width: "1px",
              backgroundColor: colors.border.subtle,
              pointerEvents: "none",
            },
          }}
        >
          {!isSuccess ? (
            <>
              <MotionBox {...REVEAL} transition={REVEAL_TRANSITION} sx={{ mb: 4 }} data-support-form-anchor>
                <Typography sx={{ fontSize: "18px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>
                  Submit a ticket
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "rgba(240,236,228,0.28)", lineHeight: 1.6 }}>
                  Tell us what happened. The more detail, the faster we can help.
                </Typography>
              </MotionBox>

              <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.08 }} sx={fieldGroupSx}>
                <Typography sx={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(240,236,228,0.28)", mb: 1 }}>
                  Category <Box component="span" sx={{ color: colors.primary.main, fontSize: "11px" }}>*</Box>
                </Typography>
                {appeal ? (
                  <Typography sx={{ fontSize: "12px", color: "rgba(240,236,228,0.52)", lineHeight: 1.6, mb: 1.5 }}>
                    This ticket will include your leaver penalty match context automatically.
                  </Typography>
                ) : null}
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                  {CATEGORIES.map((item) => {
                    const active = category === item.key;
                    return (
                      <Box
                        key={item.key}
                        component="button"
                        type="button"
                        disabled={!!appeal}
                        onClick={() => setCategory(item.key)}
                        sx={{
                          p: "10px 14px",
                          background: active ? "rgba(232,163,61,0.08)" : "rgba(255,255,255,0.025)",
                          border: `1px solid ${active ? colors.primary.main : colors.border.subtle}`,
                          cursor: "pointer",
                          textAlign: "left",
                          color: active ? colors.primary.main : "rgba(240,236,228,0.52)",
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "11px",
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          transition: "border-color .2s, background .2s",
                          opacity: appeal ? 0.55 : 1,
                          cursor: appeal ? "not-allowed" : "pointer",
                          "&:hover": appeal
                            ? {}
                            : { borderColor: "rgba(232,163,61,0.3)", background: "rgba(232,163,61,0.04)" },
                        }}
                      >
                        {item.label}
                      </Box>
                    );
                  })}
                </Box>
              </MotionBox>

              <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.12 }} sx={fieldGroupSx}>
                <FieldLabel label="Email address" required />
                <FieldInput
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
                  placeholder="your@email.com"
                  autoComplete="email"
                  disabled={!!user?.email}
                  error={(submitAttempted || touched.email) && Boolean(errors.email)}
                />
                {(submitAttempted || touched.email) && errors.email ? (
                  <Typography sx={{ mt: 1, fontSize: "11px", color: DANGER, lineHeight: 1.5 }}>
                    {errors.email}
                  </Typography>
                ) : null}
              </MotionBox>

              <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.16 }} sx={fieldGroupSx}>
                <FieldLabel label="Subject" required />
                <FieldInput
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, subject: true }))}
                  placeholder="Brief summary of the issue"
                  maxLength={120}
                  error={(submitAttempted || touched.subject) && Boolean(errors.subject)}
                />
                {(submitAttempted || touched.subject) && errors.subject ? (
                  <Typography sx={{ mt: 1, fontSize: "11px", color: DANGER, lineHeight: 1.5 }}>
                    {errors.subject}
                  </Typography>
                ) : null}
              </MotionBox>

              <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.2 }} sx={fieldGroupSx}>
                <FieldLabel label="Description" required />
                <Box
                  component="textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, description: true }))}
                  placeholder="Describe your issue in as much detail as possible. Include steps to reproduce, error messages, match IDs, or any other relevant context."
                  maxLength={4000}
                  sx={{
                    display: "block",
                    width: "100%",
                    maxWidth: "100%",
                    minHeight: 120,
                    resize: "vertical",
                    overflowX: "hidden",
                    overflowY: "auto",
                    boxSizing: "border-box",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    p: "12px 14px",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "14px",
                    lineHeight: 1.7,
                    color: colors.text.primary,
                    background: "rgba(255,255,255,0.025)",
                    border: `1px solid ${
                      (submitAttempted || touched.description) && errors.description
                        ? DANGER
                        : colors.border.subtle
                    }`,
                    outline: "none",
                    transition: "border-color .2s, background .2s",
                    "&:focus": {
                      borderColor:
                        (submitAttempted || touched.description) && errors.description
                          ? DANGER
                          : "rgba(232,163,61,0.5)",
                      background: "rgba(232,163,61,0.03)",
                    },
                    "&::placeholder": { color: "rgba(240,236,228,0.28)" },
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                    mt: 1,
                  }}
                >
                  {(submitAttempted || touched.description) && errors.description ? (
                    <Typography sx={{ flex: 1, fontSize: "11px", color: DANGER, lineHeight: 1.5 }}>
                      {errors.description}
                    </Typography>
                  ) : (
                    <Box sx={{ flex: 1 }} />
                  )}
                  <Typography
                    sx={{
                      flexShrink: 0,
                      fontSize: "10px",
                      color: "rgba(240,236,228,0.28)",
                      lineHeight: 1.5,
                    }}
                  >
                    {description.length} / 4000
                  </Typography>
                </Box>
              </MotionBox>

              <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.24 }} sx={fieldGroupSx}>
                <FieldLabel
                  label="Attachments"
                  suffix={<Box component="span" sx={{ textTransform: "none", letterSpacing: 0, fontWeight: 400, fontSize: "9px", color: "rgba(240,236,228,0.28)" }}>optional</Box>}
                />
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    addFiles(Array.from(e.dataTransfer.files || []));
                  }}
                  sx={{
                    border: `1px dashed ${dragOver ? "rgba(232,163,61,0.3)" : "rgba(255,255,255,0.12)"}`,
                    p: "28px 20px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "border-color .25s, background .25s",
                    background: dragOver ? "rgba(232,163,61,0.04)" : "transparent",
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx,.txt,.log"
                    onChange={(e) => {
                      addFiles(Array.from(e.target.files || []));
                      e.target.value = "";
                    }}
                    style={{ display: "none" }}
                  />
                  <Box sx={{ width: 40, height: 40, mx: "auto", mb: 1.5, border: `1px solid ${colors.border.subtle}`, display: "flex", alignItems: "center", justifyContent: "center", color: colors.primary.main }}>
                    <Icon size={18}>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </Icon>
                  </Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "rgba(240,236,228,0.52)", mb: 0.5 }}>
                    Drop files here
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "rgba(240,236,228,0.28)" }}>
                    Images, videos, documents -{" "}
                    <Box component="span" sx={{ color: colors.primary.main }}>
                      Browse files
                    </Box>
                  </Typography>
                </Box>

                {attachments.length > 0 ? (
                  <Box sx={{ mt: 1.25, display: "flex", flexDirection: "column", gap: "6px" }}>
                    {attachments.map((file, index) => {
                      const typeClass = fileTypeClass(file.type);
                      return (
                        <Box key={`${file.name}-${file.size}-${index}`} sx={{ display: "flex", alignItems: "center", gap: 1.25, p: "8px 12px", background: "rgba(255,255,255,0.025)", border: `1px solid ${colors.border.subtle}` }}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: `1px solid ${colors.border.subtle}`,
                              color: typeClass === "img" ? colors.primary.main : typeClass === "vid" ? "#A78BFA" : "#60A5FA",
                            }}
                          >
                            <Icon size={13}>{fileTypeIcon(file.type)}</Icon>
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {file.name}
                            </Typography>
                            <Typography sx={{ fontSize: "10px", color: "rgba(240,236,228,0.28)" }}>
                              {formatSize(file.size)}
                            </Typography>
                          </Box>
                          <Box
                            component="button"
                            type="button"
                            onClick={() => removeAttachment(index)}
                            sx={{
                              p: 0.5,
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "rgba(240,236,228,0.28)",
                              display: "flex",
                              "&:hover": { color: "#dc2626" },
                            }}
                          >
                            <Icon size={14}>
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </Icon>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ) : null}

                {fileError ? (
                  <Typography sx={{ mt: 0.75, fontSize: "11px", color: "#dc2626" }}>{fileError}</Typography>
                ) : null}
              </MotionBox>

              <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.28 }}>
                {showValidationAlert ? (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: "12px 14px",
                      border: `1px solid rgba(220,38,38,0.45)`,
                      background: "linear-gradient(180deg, rgba(220,38,38,0.12), rgba(220,38,38,0.04))",
                    }}
                  >
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#fca5a5", mb: 0.4 }}>
                      Please fix these fields
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#fecaca", lineHeight: 1.6 }}>
                      {[errors.email, errors.subject, errors.description].filter(Boolean).join(" ")}
                    </Typography>
                  </Box>
                ) : null}
                {submitError ? (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: "12px 14px",
                      border: `1px solid rgba(220,38,38,0.45)`,
                      background: "linear-gradient(180deg, rgba(220,38,38,0.12), rgba(220,38,38,0.04))",
                    }}
                  >
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#fca5a5", mb: 0.4 }}>
                      Could not submit ticket
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#fecaca", lineHeight: 1.6 }}>
                      {submitError}
                    </Typography>
                  </Box>
                ) : null}
                <Box
                  component="button"
                  type="button"
                  disabled={isSubmitting}
                  onClick={onSubmit}
                  sx={{
                    width: "100%",
                    mt: 3,
                    p: "16px",
                    border: "none",
                    clipPath: "polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)",
                    background: colors.primary.main,
                    color: HERO_BG,
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    opacity: isSubmitting ? 0.7 : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    transition: "transform .25s ease, box-shadow .25s ease",
                    "&:hover": isSubmitting ? {} : { transform: "translateY(-2px)", boxShadow: "0 10px 32px rgba(232,163,61,0.22)" },
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Box
                        component="span"
                        sx={{
                          width: 14,
                          height: 14,
                          border: "2px solid rgba(8,8,8,0.35)",
                          borderTopColor: HERO_BG,
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                          "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                        }}
                      />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Icon size={16}>
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </Icon>
                      Submit ticket
                    </>
                  )}
                </Box>
                <Typography sx={{ mt: 2, textAlign: "center", fontSize: "11px", color: "rgba(240,236,228,0.28)", lineHeight: 1.6 }}>
                  Your data is handled under our{" "}
                  <Box component={Link} to="/data-protection-policy-and-gdpr" sx={{ color: colors.primary.main, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                    GDPR policy
                  </Box>
                  . Need faster help? Join our{" "}
                  <Box component="a" href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer" sx={{ color: colors.primary.main, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                    Discord
                  </Box>
                  .
                </Typography>
              </MotionBox>
            </>
          ) : (
            <Box
              sx={{
                minHeight: 400,
                p: "48px 24px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <Box sx={{ width: 72, height: 72, borderRadius: "50%", border: "1px solid rgba(22,163,74,0.4)", background: "rgba(22,163,74,0.06)", display: "flex", alignItems: "center", justifyContent: "center", mb: 2.5, color: "#4ade80" }}>
                <Icon size={32}>
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <path d="M22 4 12 14.01l-3-3" />
                </Icon>
              </Box>
              <Typography
                data-support-form-anchor
                sx={{ fontSize: "20px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}
              >
                Ticket submitted
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "rgba(240,236,228,0.52)", lineHeight: 1.7, maxWidth: "22rem", mb: 3 }}>
                We&apos;ve received your report and will follow up at the email address you provided.
                {user ? " You can track this ticket from your account." : " Keep this number for reference."}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                  p: "10px 14px",
                  border: `1px solid ${colors.border.subtle}`,
                  background: "rgba(255,255,255,0.02)",
                  mb: 3,
                  maxWidth: "100%",
                }}
              >
                <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.14em", color: "rgba(240,236,228,0.4)" }}>
                  Ticket number
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    color: colors.primary.main,
                    fontFamily: "ui-monospace, monospace",
                  }}
                >
                  {ticketNumber}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  onClick={copyTicketNumber}
                  aria-label="Copy ticket number"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.75,
                    border: `1px solid ${copied ? "rgba(22,163,74,0.45)" : colors.border.subtle}`,
                    background: copied ? "rgba(22,163,74,0.08)" : "rgba(255,255,255,0.03)",
                    color: copied ? "#4ade80" : colors.text.secondary,
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    transition: "border-color .2s, background .2s, color .2s",
                    "&:hover": {
                      borderColor: "rgba(232,163,61,0.35)",
                      color: colors.primary.main,
                    },
                  }}
                >
                  <Icon size={14}>
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </Icon>
                  {copied ? "Copied" : "Copy"}
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, width: "100%", maxWidth: 280 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={startNewTicket}
                  sx={{
                    ...btnGhostSx,
                    width: "100%",
                    borderColor: "rgba(232,163,61,0.35)",
                    color: colors.text.primary,
                  }}
                >
                  Submit another ticket
                </Box>
                {user ? (
                  <Box component={Link} to="/account/tickets" sx={{ ...btnGhostSx, width: "100%" }}>
                    View my tickets
                  </Box>
                ) : null}
              </Box>
            </Box>
          )}
        </Box>
        </Box>
      </Box>
    </Box>
  );
}

function FieldLabel({ label, required = false, suffix = null }) {
  return (
    <Typography sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "9px", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(240,236,228,0.28)", mb: 1.25 }}>
      <Box component="span">
        {label}{" "}
        {required ? <Box component="span" sx={{ color: colors.primary.main, fontSize: "11px" }}>*</Box> : null}
      </Box>
      {suffix}
    </Typography>
  );
}

function FieldInput({ error = false, disabled = false, ...props }) {
  return (
    <Box
      component="input"
      disabled={disabled}
      {...props}
      sx={{
        width: "100%",
        mb: 0,
        p: "12px 14px",
        background: "rgba(255,255,255,0.025)",
        border: `1px solid ${error ? DANGER : colors.border.subtle}`,
        color: colors.text.primary,
        fontFamily: "'Inter', sans-serif",
        fontSize: "14px",
        outline: "none",
        transition: "border-color .2s, background .2s",
        "&:focus": {
          borderColor: error ? DANGER : "rgba(232,163,61,0.5)",
          background: "rgba(232,163,61,0.03)",
        },
        "&::placeholder": { color: "rgba(240,236,228,0.28)" },
        "&:disabled": { opacity: 0.65, cursor: "not-allowed" },
      }}
    />
  );
}
